import React, { useState, useEffect } from "react";
import { Modal, Input, Form } from "antd";
import {
  getAlert,
  insertData,
  getDataWithComp,
} from "../../../utils/SamuiUtils";

export default function AddColorModal({
  open,
  onClose,
  onSuccess,
  existingColors = [],
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const validateColourCode = (_, value) => {
    if (!value) return Promise.reject(new Error("กรุณากรอกรหัสสี"));

    // ตรวจสอบว่าเป็นภาษาอังกฤษ ตัวเลข และเครื่องหมาย - _ เท่านั้น
    const englishOnly = /^[A-Za-z0-9-_]+$/;
    if (!englishOnly.test(value)) {
      return Promise.reject(
        new Error("รหัสสีต้องเป็นภาษาอังกฤษ ตัวเลข และเครื่องหมาย - _ เท่านั้น")
      );
    }

    const isDuplicate = existingColors.some(
      (c) =>
        String(c.code || "").toUpperCase() === String(value || "").toUpperCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error("รหัสสีนี้มีอยู่ในระบบแล้ว"));
    }

    return Promise.resolve();
  };

  const validateColourName = (_, value) => {
    if (!value) return Promise.reject(new Error("กรุณากรอกชื่อสี"));

    const isDuplicate = existingColors.some(
      (c) =>
        String(c.name || "").toLowerCase() === String(value || "").toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error("ชื่อสีนี้มีอยู่ในระบบแล้ว"));
    }

    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      setLoading(true);

      const compId = localStorage.getItem("company");

      // ✅ แปลงรหัสสีเป็นตัวพิมพ์ใหญ่
      const colourCodeUpper = values.Colour_Code.trim().toUpperCase();

      // ✅ ตรวจสอบซ้ำกับ DB
      let codeCheck, nameCheck;
      try {
        codeCheck = await getDataWithComp(
          "Tb_Set_ItmColour",
          `AND Colour_Code='${colourCodeUpper}' AND Colour_Status='Y'`
        );
      } catch (e) {
        console.error("❌ Error checking Colour_Code:", e);
        getAlert("FAILED", "ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง");
        setLoading(false);
        return;
      }

      if (codeCheck && codeCheck.length > 0) {
        getAlert("FAILED", `รหัสสี "${colourCodeUpper}" มีอยู่ในระบบแล้ว`);
        setLoading(false);
        return;
      }

      try {
        nameCheck = await getDataWithComp(
          "Tb_Set_ItmColour",
          `AND Colour_Name='${values.Colour_Name.trim()}' AND Colour_Status='Y'`
        );
      } catch (e) {
        console.error("❌ Error checking Colour_Name:", e);
        getAlert("FAILED", "ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง");
        setLoading(false);
        return;
      }

      const fields = "Colour_Code,Colour_Name,Colour_Status,Comp_Id";
      const valuesArray = [
        [
          colourCodeUpper,
          values.Colour_Name.trim(),
          "Y",
          Number(compId),
        ],
      ];

      const response = await insertData(
        "Tb_Set_ItmColour",
        fields,
        valuesArray
      );

      if (response && response.ok !== false) {
        const newColorId = response.data?.[0] || response.insertId;

        // ✅ รอให้ onSuccess ทำงานเสร็จก่อนปิด modal
        await onSuccess?.(newColorId);

        getAlert("OK", "เพิ่มสีสำเร็จ");
        onClose();
      } else {
        console.error("❌ Insert color failed:", response);
        const errorMsg =
          response?.error || response?.message || "ไม่สามารถเพิ่มสีได้";
        getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error("❌ Error adding color:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.message ||
        "เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์";
      getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="เพิ่มสีใหม่"
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={480}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="Colour_Code"
          label="รหัสสี (ภาษาอังกฤษ)"
          rules={[{ validator: validateColourCode }]}
          extra={
            <span style={{ color: "#f97316", fontSize: "12px" }}>
              ⚠️มีผลกับการสร้างรหัสสินค้า
            </span>
          }
        >
          <Input
            placeholder="เช่น RED, BLU, GRN"
            maxLength={20}
            style={{ textTransform: "uppercase", height: "35px" }}
          />
        </Form.Item>

        <Form.Item
          name="Colour_Name"
          label="ชื่อสี"
          rules={[{ validator: validateColourName }]}
        >
          <Input
            placeholder="เช่น แดง, น้ำเงิน, เขียว"
            maxLength={50}
            size="large"
            style={{ height: "35px" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
